/**
 * AdminFinance.jsx — UI-C
 * =======================
 *
 * TreatX Admin cross-Provider finance monitoring surface. Composes:
 *   GET /api/finance/admin/overview
 *     -> providers[]   (per clinic × PSP × currency canonical position)
 *     -> settlements[] (canonical settle-milestone psp_operations rows)
 *     -> exceptions[]  (failed psp_operations + failed settlement_authorizations)
 *   GET /api/finance/provider/{clinic_id}/booking/{booking_id}/detail
 *     -> Booking financial drill-down (same UI-B endpoint)
 *
 * READ-ONLY. Admin does NOT initiate normal settlement.
 * No manual settlement form.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw, X, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { api } from '../../utils/api';
import { Pill } from '../../components/journey-ux';
import DateField from '../../components/DateField';
import AccountingGradeReports from '../../components/AccountingGradeReports';
import CommissionReceivablesPanel from './CommissionReceivablesPanel';

const fmtMinor = (minor, currency) => {
  if (minor == null) return '—';
  const value = Number(minor) / 100;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency', currency: currency || 'EUR',
      maximumFractionDigits: 2, minimumFractionDigits: 2,
    }).format(value);
  } catch { return `${value.toFixed(2)} ${currency || ''}`.trim(); }
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toISOString().replace('T', ' ').slice(0, 16); } catch { return '—'; }
};

const OP_TONE = { completed: 'emerald', pending: 'amber', failed: 'rose', executed: 'emerald', authorized: 'sky' };

// ---------------------------------------------------------------------------
// Booking drill-down drawer (reuses UI-B endpoint — canonical booking truth).
// ---------------------------------------------------------------------------
const AdminBookingDrawer = ({ clinicId, bookingId, onClose }) => {
  const { t } = useTranslation();
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    api.get(`/finance/provider/${clinicId}/booking/${bookingId}/detail`)
      .then((res) => { if (!cancelled) setDetail(res?.data || res); })
      // P0-FIN-ISOLATION-01 — a refused read must REPLACE the rendered figures.
      .catch((err) => { if (!cancelled) { setDetail(null); setError(err?.response?.data?.message || err?.message || t('adminFinance.detail.errorGeneric')); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clinicId, bookingId, t]);

  const currency = detail?.currency || detail?.contract_currency;

  return (
    <div className="fixed inset-0 z-40 flex" data-testid="admin-finance-drawer">
      <button type="button" aria-label={t('adminFinance.detail.closeAria')}
              className="flex-1 bg-slate-900/30" onClick={onClose} />
      <aside className="w-[min(760px,100%)] bg-white border-l border-slate-200 overflow-y-auto">
        <header className="px-5 py-3 border-b border-slate-200 flex items-center gap-2 sticky top-0 bg-white z-10">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              {t('adminFinance.detail.title')}
            </div>
            <div className="text-[13px] font-semibold text-slate-800 truncate" data-testid="admin-finance-drawer-provider">
              {detail?.provider_name || clinicId}
            </div>
            <div className="text-[10px] text-slate-400 font-mono truncate">
              {detail?.provider_name && `${clinicId} · `}{bookingId}
            </div>
          </div>
          <button type="button" onClick={onClose} className="ml-auto p-1.5 rounded hover:bg-slate-100 text-slate-500"
                  data-testid="admin-finance-drawer-close">
            <X className="h-4 w-4" />
          </button>
        </header>

        {loading && <div className="p-6 text-[13px] text-slate-500">{t('adminFinance.detail.loading')}</div>}
        {error && !loading && <div className="p-6 text-[13px] text-rose-700">{error}</div>}

        {detail && !loading && !error && (
          <div className="p-5 space-y-4">
            <section className="rounded border border-slate-200 bg-white p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
                {t('adminFinance.detail.rollup')}
              </div>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[12px]">
                <dt className="text-sky-700">{t('adminFinance.terms.customerFundsReceived')}</dt>
                <dd className="text-right text-sky-900 font-medium">{fmtMinor(detail.rollup.customer_funds_received_minor_units, currency)}</dd>
                <dt className="text-slate-500">{t('adminFinance.terms.grossRevenue')}</dt>
                <dd className="text-right text-slate-800 font-medium">{fmtMinor(detail.rollup.earned_gross_minor_units, currency)}</dd>
                <dt className="text-slate-500">{t('adminFinance.terms.feesAndTaxes')}</dt>
                <dd className="text-right text-slate-800 font-medium">{fmtMinor(detail.rollup.fees_and_taxes_minor_units, currency)}</dd>
                <dt className="text-slate-800 font-semibold">{t('adminFinance.terms.netEarnings')}</dt>
                <dd className="text-right text-slate-900 font-semibold">{fmtMinor(detail.rollup.net_earnings_minor_units, currency)}</dd>
                <dt className="text-amber-700">{t('adminFinance.terms.settlementPending')}</dt>
                <dd className="text-right text-amber-800 font-medium">{fmtMinor(detail.rollup.settlement_pending_minor_units, currency)}</dd>
                <dt className="text-emerald-700">{t('adminFinance.terms.settled')}</dt>
                <dd className="text-right text-emerald-800 font-medium">{fmtMinor(detail.rollup.settled_minor_units, currency)}</dd>
                <dt className="text-rose-700">{t('adminFinance.terms.reversed')}</dt>
                <dd className="text-right text-rose-800 font-medium">{fmtMinor(detail.rollup.reversed_minor_units, currency)}</dd>
              </dl>
            </section>

            <section>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
                {t('adminFinance.detail.milestones')}
              </div>
              {detail.milestones.length === 0 ? (
                <div className="text-[12px] text-slate-500">{t('adminFinance.detail.milestonesEmpty')}</div>
              ) : (
                <div className="overflow-x-auto border border-slate-200 rounded">
                  <table className="w-full text-[12px]">
                    <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wider">
                      <tr>
                        <th className="text-left px-2 py-1.5">#</th>
                        <th className="text-left px-2 py-1.5">{t('adminFinance.detail.col.label')}</th>
                        <th className="text-left px-2 py-1.5">{t('adminFinance.detail.col.status')}</th>
                        <th className="text-right px-2 py-1.5">{t('adminFinance.detail.col.amount')}</th>
                        <th className="text-right px-2 py-1.5">{t('adminFinance.detail.col.net')}</th>
                        <th className="text-left px-2 py-1.5">{t('adminFinance.detail.col.settlement')}</th>
                        <th className="text-left px-2 py-1.5">{t('adminFinance.detail.col.settledAt')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.milestones.map((m) => (
                        <tr key={m.milestone_id} className="border-t border-slate-100">
                          <td className="px-2 py-1.5 text-slate-800 font-mono">{m.seq}</td>
                          <td className="px-2 py-1.5 text-slate-800">{m.label}</td>
                          <td className="px-2 py-1.5"><Pill tone="slate">{t(`common.status.${m.status}`, { defaultValue: m.status })}</Pill></td>
                          <td className="px-2 py-1.5 text-right">{fmtMinor(m.amount_minor_units, currency)}</td>
                          <td className="px-2 py-1.5 text-right text-slate-900 font-semibold">{fmtMinor(m.provider_net_minor_units, currency)}</td>
                          <td className="px-2 py-1.5">
                            {m.settlement_status
                              ? <Pill tone={OP_TONE[m.settlement_status] || 'slate'}>{m.settlement_status}</Pill>
                              : <span className="text-slate-400">—</span>}
                          </td>
                          <td className="px-2 py-1.5">{fmtDate(m.settled_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </div>
        )}
      </aside>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Filter helpers — small, dependency-free primitives so we don't build a
// reporting engine. All filtering is client-side over the single canonical
// `/finance/admin/overview` payload. Sorting is by header click.
// ---------------------------------------------------------------------------
const uniqueSorted = (rows, key) => {
  const s = new Set();
  (rows || []).forEach((r) => { if (r[key]) s.add(r[key]); });
  return Array.from(s).sort();
};
const includesCI = (hay, needle) =>
  hay && needle && String(hay).toLowerCase().includes(String(needle).toLowerCase());
const rowInDateRange = (iso, from, to) => {
  if (!iso) return !from && !to;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return true;
  if (from) {
    const f = new Date(`${from}T00:00:00Z`);
    if (d < f) return false;
  }
  if (to) {
    const tEnd = new Date(`${to}T23:59:59Z`);
    if (d > tEnd) return false;
  }
  return true;
};
const SortIcon = ({ dir }) => (
  <span aria-hidden="true" className="ml-1 inline-block text-[9px] text-slate-400">
    {dir === 'asc' ? '▲' : dir === 'desc' ? '▼' : '↕'}
  </span>
);
const SortHeader = ({ label, colKey, sort, setSort, align = 'left', testid }) => {
  const active = sort?.key === colKey;
  const dir = active ? sort.dir : null;
  const nextDir = active ? (dir === 'asc' ? 'desc' : (dir === 'desc' ? null : 'asc')) : 'asc';
  const nextSort = nextDir ? { key: colKey, dir: nextDir } : { key: null, dir: null };
  return (
    <th className={`text-${align} px-2 py-1.5`}>
      <button type="button"
              onClick={() => setSort(nextSort)}
              className={`inline-flex items-center hover:text-slate-800 ${active ? 'text-slate-900' : ''}`}
              data-testid={testid}>
        {label} <SortIcon dir={dir} />
      </button>
    </th>
  );
};

// ---------------------------------------------------------------------------
// Main Admin Finance page
// ---------------------------------------------------------------------------
const AdminFinance = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('providers');
  const [filterQ, setFilterQ] = useState('');
  const [drawer, setDrawer] = useState(null);

  // Per-column filter state (dedicated) + generic search retained.
  const [providersFilter, setProvidersFilter] = useState({
    provider: '', psp: '', currency: '', sort: { key: null, dir: null },
  });
  const [settlementsFilter, setSettlementsFilter] = useState({
    provider: '', booking: '', patient: '', psp: '', currency: '', status: '',
    from: '', to: '', sort: { key: 'requested_at', dir: 'desc' },
  });
  const [exceptionsFilter, setExceptionsFilter] = useState({
    provider: '', booking: '', patient: '', kind: '', psp: '', currency: '',
    from: '', to: '', sort: { key: 'at_utc', dir: 'desc' },
  });

  const load = useCallback(() => {
    setLoading(true); setError(null);
    api.get('/finance/admin/overview')
      .then((res) => setOverview(res?.data || res))
      // P0-FIN-ISOLATION-01 — clear the cross-provider overview on refusal.
      .catch((err) => { setOverview(null); setError(err?.response?.data?.message || err?.message || t('adminFinance.errorGeneric')); })
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => { load(); }, [load]);

  // Categorical dropdown domains sourced from ACTUAL rows in the payload —
  // no hardcoded taxonomy, no invented enum.
  const providerRows = overview?.providers || [];
  const settlementRows = overview?.settlements || [];
  const exceptionRows = overview?.exceptions || [];
  const providerPsps = useMemo(() => uniqueSorted(providerRows, 'psp_provider'), [providerRows]);
  const providerCurrencies = useMemo(() => uniqueSorted(providerRows, 'currency'), [providerRows]);
  const settlementPsps = useMemo(() => uniqueSorted(settlementRows, 'psp_provider'), [settlementRows]);
  const settlementCurrencies = useMemo(() => uniqueSorted(settlementRows, 'currency'), [settlementRows]);
  const settlementStatuses = useMemo(() => uniqueSorted(settlementRows, 'status'), [settlementRows]);
  const exceptionKinds = useMemo(() => uniqueSorted(exceptionRows, 'kind'), [exceptionRows]);
  const exceptionPsps = useMemo(() => uniqueSorted(exceptionRows, 'psp_provider'), [exceptionRows]);
  const exceptionCurrencies = useMemo(() => uniqueSorted(exceptionRows, 'currency'), [exceptionRows]);

  const applySort = (rows, sort) => {
    if (!sort?.key || !sort?.dir) return rows;
    const key = sort.key;
    const dir = sort.dir === 'asc' ? 1 : -1;
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = a?.[key]; const bv = b?.[key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      // Date-like string?
      if (/^\d{4}-\d{2}-\d{2}/.test(String(av)) && /^\d{4}-\d{2}-\d{2}/.test(String(bv))) {
        return (new Date(av) - new Date(bv)) * dir;
      }
      return String(av).localeCompare(String(bv)) * dir;
    });
    return copy;
  };

  const filteredProviders = useMemo(() => {
    const q = filterQ.trim().toLowerCase();
    const f = providersFilter;
    let rows = providerRows.filter((r) => {
      if (f.provider) {
        if (!includesCI(r.provider_name, f.provider) && !includesCI(r.clinic_id, f.provider)) return false;
      }
      if (f.psp && r.psp_provider !== f.psp) return false;
      if (f.currency && r.currency !== f.currency) return false;
      if (q) {
        const hay = [r.provider_name, r.clinic_id, r.psp_provider, r.currency]
          .filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    rows = applySort(rows, f.sort);
    return rows;
  }, [providerRows, providersFilter, filterQ]);

  const filteredSettlements = useMemo(() => {
    const q = filterQ.trim().toLowerCase();
    const f = settlementsFilter;
    let rows = settlementRows.filter((r) => {
      if (f.provider) {
        if (!includesCI(r.provider_name, f.provider) && !includesCI(r.clinic_id, f.provider)) return false;
      }
      if (f.booking && !includesCI(r.booking_id, f.booking)) return false;
      if (f.patient) {
        if (!includesCI(r.patient_name, f.patient) && !includesCI(r.patient_id, f.patient)) return false;
      }
      if (f.psp && r.psp_provider !== f.psp) return false;
      if (f.currency && r.currency !== f.currency) return false;
      if (f.status && r.status !== f.status) return false;
      if (f.from || f.to) {
        if (!rowInDateRange(r.requested_at || r.completed_at || r.failed_at, f.from, f.to)) return false;
      }
      if (q) {
        const hay = [r.provider_name, r.clinic_id, r.psp_provider, r.currency,
          r.booking_id, r.patient_name, r.patient_id, r.status, r.psp_transfer_id]
          .filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    rows = applySort(rows, f.sort);
    return rows;
  }, [settlementRows, settlementsFilter, filterQ]);

  const filteredExceptions = useMemo(() => {
    const q = filterQ.trim().toLowerCase();
    const f = exceptionsFilter;
    let rows = exceptionRows.filter((r) => {
      if (f.provider) {
        if (!includesCI(r.provider_name, f.provider) && !includesCI(r.clinic_id, f.provider)) return false;
      }
      if (f.booking && !includesCI(r.booking_id, f.booking)) return false;
      if (f.patient) {
        if (!includesCI(r.patient_name, f.patient) && !includesCI(r.patient_id, f.patient)) return false;
      }
      if (f.kind && r.kind !== f.kind) return false;
      if (f.psp && r.psp_provider !== f.psp) return false;
      if (f.currency && r.currency !== f.currency) return false;
      if (f.from || f.to) {
        if (!rowInDateRange(r.at_utc, f.from, f.to)) return false;
      }
      if (q) {
        const hay = [r.provider_name, r.clinic_id, r.psp_provider, r.currency,
          r.booking_id, r.patient_name, r.patient_id, r.kind, r.detail, r.status]
          .filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    rows = applySort(rows, f.sort);
    return rows;
  }, [exceptionRows, exceptionsFilter, filterQ]);

  const clearProviders = () => setProvidersFilter({ provider: '', psp: '', currency: '', sort: { key: null, dir: null } });
  const clearSettlements = () => setSettlementsFilter({
    provider: '', booking: '', patient: '', psp: '', currency: '', status: '',
    from: '', to: '', sort: { key: 'requested_at', dir: 'desc' },
  });
  const clearExceptions = () => setExceptionsFilter({
    provider: '', booking: '', patient: '', kind: '', psp: '', currency: '',
    from: '', to: '', sort: { key: 'at_utc', dir: 'desc' },
  });

  const TABS = [
    { id: 'providers',   label: t('adminFinance.tabs.providers'),   count: overview?.providers?.length ?? 0 },
    { id: 'settlements', label: t('adminFinance.tabs.settlements'), count: overview?.settlements?.length ?? 0 },
    { id: 'exceptions',  label: t('adminFinance.tabs.exceptions'),  count: overview?.exceptions?.length ?? 0 },
    {
      id: 'subscriptions',
      label: t('adminFinance.tabs.subscriptions'),
      count: overview?.subscriptions?.by_currency?.length ?? 0,
    },
    { id: 'reports', label: t('adminFinance.tabs.reports'), count: '' },
    // TREATX MIXED SETTLEMENT — additional commission receivables arising
    // from provider-declared direct collections.
    { id: 'receivables', label: t('adminFinance.tabs.receivables'), count: '' },
  ];

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-slate-50 flex flex-col" data-testid="admin-finance">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="px-5 py-2 flex items-center gap-3">
          <button type="button" onClick={() => navigate('/treatx-admin/dashboard')}
                  className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-800"
                  data-testid="admin-finance-back">
            <ArrowLeft className="h-3 w-3" />
            {t('adminFinance.back')}
          </button>
          <div className="ml-2">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              {t('adminFinance.sectionLabel')}
            </div>
            <div className="text-[13px] font-semibold text-slate-800" data-testid="admin-finance-title">
              {t('adminFinance.title')}
            </div>
          </div>
          <button type="button" onClick={load}
                  className="ml-auto inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded border border-slate-200 hover:border-slate-300 text-slate-700"
                  data-testid="admin-finance-refresh">
            <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
            {t('adminFinance.refresh')}
          </button>
        </div>
        <nav className="px-5 border-t border-slate-100 flex items-center gap-0 overflow-x-auto" data-testid="admin-finance-tabs">
          {TABS.map((v) => (
            <button key={v.id} type="button" onClick={() => setTab(v.id)}
                    data-testid={`admin-finance-tab-${v.id}`}
                    className={`text-[12px] px-3 py-2 border-b-2 -mb-px whitespace-nowrap flex-shrink-0 ${
                      tab === v.id ? 'border-slate-900 text-slate-900 font-semibold' : 'border-transparent text-slate-500 hover:text-slate-800'
                    }`}>
              {v.label}
              <span className={`ml-1.5 text-[10px] ${tab === v.id ? 'text-slate-600' : 'text-slate-400'}`}>{v.count}</span>
            </button>
          ))}
          <div className="ml-auto px-2 py-1.5">
            <input type="text" value={filterQ} onChange={(iso) => setFilterQ(iso)}
                   placeholder={t('adminFinance.filters.quickSearch')}
                   className="pl-2 pr-2 py-1 border border-slate-200 rounded text-[12px] focus:outline-none focus:border-slate-400 w-64"
                   data-testid="admin-finance-filter" />
          </div>
        </nav>
      </header>

      <div className="p-5">
        {loading && <div className="text-[12px] text-slate-500" data-testid="admin-finance-loading">{t('adminFinance.loading')}</div>}
        {error && !loading && <div className="text-[12px] text-rose-700">{error}</div>}

        {!loading && !error && tab === 'providers' && (
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2 items-center bg-white border border-slate-200 rounded px-2 py-2" data-testid="providers-filters">
              <input type="text" value={providersFilter.provider}
                     onChange={(e) => setProvidersFilter((f) => ({ ...f, provider: e.target.value }))}
                     placeholder={t('adminFinance.filters.providerSearch')}
                     className="px-2 py-1 border border-slate-200 rounded text-[12px] w-56"
                     data-testid="providers-filter-provider" />
              <select value={providersFilter.psp}
                      onChange={(e) => setProvidersFilter((f) => ({ ...f, psp: e.target.value }))}
                      className="px-2 py-1 border border-slate-200 rounded text-[12px]"
                      data-testid="providers-filter-psp">
                <option value="">{t('adminFinance.filters.anyPsp')}</option>
                {providerPsps.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              <select value={providersFilter.currency}
                      onChange={(e) => setProvidersFilter((f) => ({ ...f, currency: e.target.value }))}
                      className="px-2 py-1 border border-slate-200 rounded text-[12px]"
                      data-testid="providers-filter-currency">
                <option value="">{t('adminFinance.filters.anyCurrency')}</option>
                {providerCurrencies.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <button type="button" onClick={clearProviders}
                      className="px-2 py-1 text-[11px] border border-slate-200 rounded text-slate-600 hover:bg-slate-50"
                      data-testid="providers-filter-clear">
                {t('adminFinance.filters.clear')}
              </button>
              <span className="ml-auto text-[11px] text-slate-500" data-testid="providers-count">
                {t('adminFinance.filters.showing', { shown: filteredProviders.length, total: providerRows.length })}
              </span>
            </div>
            <div className="overflow-x-auto border border-slate-200 rounded bg-white">
              <table className="w-full text-[12px]" data-testid="admin-finance-providers-table">
                <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wider">
                  <tr>
                    <SortHeader label={t('adminFinance.providers.col.provider')} colKey="provider_name"
                                sort={providersFilter.sort}
                                setSort={(s) => setProvidersFilter((f) => ({ ...f, sort: s }))}
                                testid="providers-sort-provider" />
                    <SortHeader label={t('adminFinance.terms.customerFundsReceived')} colKey="funded_minor_units"
                                align="right" sort={providersFilter.sort}
                                setSort={(s) => setProvidersFilter((f) => ({ ...f, sort: s }))}
                                testid="providers-sort-funded" />
                    <SortHeader label={t('adminFinance.terms.deductions')} colKey="fees_taxes_minor_units"
                                align="right" sort={providersFilter.sort}
                                setSort={(s) => setProvidersFilter((f) => ({ ...f, sort: s }))}
                                testid="providers-sort-deductions" />
                    <SortHeader label={t('adminFinance.terms.amountPayable')} colKey="net_minor_units"
                                align="right" sort={providersFilter.sort}
                                setSort={(s) => setProvidersFilter((f) => ({ ...f, sort: s }))}
                                testid="providers-sort-net" />
                    <SortHeader label={t('adminFinance.terms.settlementPending')} colKey="settlement_pending_minor_units"
                                align="right" sort={providersFilter.sort}
                                setSort={(s) => setProvidersFilter((f) => ({ ...f, sort: s }))}
                                testid="providers-sort-pending" />
                    <SortHeader label={t('adminFinance.terms.settled')} colKey="settled_minor_units"
                                align="right" sort={providersFilter.sort}
                                setSort={(s) => setProvidersFilter((f) => ({ ...f, sort: s }))}
                                testid="providers-sort-settled" />
                    <SortHeader label={t('adminFinance.terms.reversed')} colKey="reversed_minor_units"
                                align="right" sort={providersFilter.sort}
                                setSort={(s) => setProvidersFilter((f) => ({ ...f, sort: s }))}
                                testid="providers-sort-reversed" />
                    <th className="text-left px-2 py-1.5">{t('adminFinance.providers.col.settings')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProviders.length === 0 && (
                    <tr><td colSpan={8} className="px-2 py-4 text-center text-slate-500" data-testid="admin-finance-providers-empty">
                      {t('adminFinance.providers.empty')}
                    </td></tr>
                  )}
                  {filteredProviders.map((r, i) => (
                    <tr key={`${r.clinic_id}-${r.psp_provider}-${r.currency}-${i}`} className="border-t border-slate-100">
                      <td className="px-2 py-1.5" data-testid={`admin-finance-provider-cell-${r.clinic_id}`}>
                        <div className="text-slate-900 font-medium">
                          {r.provider_brand || r.provider_name || r.clinic_id}
                        </div>
                        {r.provider_legal_entity && r.provider_legal_entity !== r.provider_brand && (
                          <div className="text-[11px] text-slate-600 truncate max-w-[240px]" title={r.provider_legal_entity}>
                            {r.provider_legal_entity}
                          </div>
                        )}
                        {!r.provider_brand && !r.provider_legal_entity && (
                          <div className="text-[11px] text-amber-700 italic" data-testid={`admin-finance-provider-unknown-${r.clinic_id}`}>
                            Provider profile not yet completed
                          </div>
                        )}
                        <div className="text-[10px] text-slate-400 font-mono">
                          Provider ID: {r.clinic_id}
                        </div>
                      </td>
                      <td className="px-2 py-1.5 text-right text-sky-900">{fmtMinor(r.funded_minor_units, r.currency)}</td>
                      <td className="px-2 py-1.5 text-right">{fmtMinor(r.fees_taxes_minor_units, r.currency)}</td>
                      <td className="px-2 py-1.5 text-right text-slate-900 font-semibold">{fmtMinor(r.net_minor_units, r.currency)}</td>
                      <td className="px-2 py-1.5 text-right text-amber-800">{fmtMinor(r.settlement_pending_minor_units, r.currency)}</td>
                      <td className="px-2 py-1.5 text-right text-emerald-800">{fmtMinor(r.settled_minor_units, r.currency)}</td>
                      <td className="px-2 py-1.5 text-right text-rose-700">{fmtMinor(r.reversed_minor_units, r.currency)}</td>
                      <td className="px-2 py-1.5 text-slate-500 text-[11px]">
                        {(r.psp_provider || '—')} · {(r.currency || '—')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!loading && !error && tab === 'settlements' && (
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2 items-center bg-white border border-slate-200 rounded px-2 py-2" data-testid="settlements-filters">
              <input type="text" value={settlementsFilter.provider}
                     onChange={(e) => setSettlementsFilter((f) => ({ ...f, provider: e.target.value }))}
                     placeholder={t('adminFinance.filters.providerSearch')}
                     className="px-2 py-1 border border-slate-200 rounded text-[12px] w-44"
                     data-testid="settlements-filter-provider" />
              <input type="text" value={settlementsFilter.patient}
                     onChange={(e) => setSettlementsFilter((f) => ({ ...f, patient: e.target.value }))}
                     placeholder={t('adminFinance.filters.patientSearch')}
                     className="px-2 py-1 border border-slate-200 rounded text-[12px] w-44"
                     data-testid="settlements-filter-patient" />
              <input type="text" value={settlementsFilter.booking}
                     onChange={(e) => setSettlementsFilter((f) => ({ ...f, booking: e.target.value }))}
                     placeholder={t('adminFinance.filters.bookingSearch')}
                     className="px-2 py-1 border border-slate-200 rounded text-[12px] w-36"
                     data-testid="settlements-filter-booking" />
              <select value={settlementsFilter.psp}
                      onChange={(e) => setSettlementsFilter((f) => ({ ...f, psp: e.target.value }))}
                      className="px-2 py-1 border border-slate-200 rounded text-[12px]"
                      data-testid="settlements-filter-psp">
                <option value="">{t('adminFinance.filters.anyPsp')}</option>
                {settlementPsps.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              <select value={settlementsFilter.currency}
                      onChange={(e) => setSettlementsFilter((f) => ({ ...f, currency: e.target.value }))}
                      className="px-2 py-1 border border-slate-200 rounded text-[12px]"
                      data-testid="settlements-filter-currency">
                <option value="">{t('adminFinance.filters.anyCurrency')}</option>
                {settlementCurrencies.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <select value={settlementsFilter.status}
                      onChange={(e) => setSettlementsFilter((f) => ({ ...f, status: e.target.value }))}
                      className="px-2 py-1 border border-slate-200 rounded text-[12px]"
                      data-testid="settlements-filter-status">
                <option value="">{t('adminFinance.filters.anyStatus')}</option>
                {settlementStatuses.map((s) => (
                  <option key={s} value={s}>{t(`common.status.${s}`, { defaultValue: s })}</option>
                ))}
              </select>
              <label className="text-[11px] text-slate-500">{t('adminFinance.filters.dateFrom')}</label>
              <DateField value={settlementsFilter.from}
                     onChange={(iso) => setSettlementsFilter((f) => ({ ...f, from: iso }))}
                     className="px-2 py-1 border border-slate-200 rounded text-[12px]"
                     data-testid="settlements-filter-from" />
              <label className="text-[11px] text-slate-500">{t('adminFinance.filters.dateTo')}</label>
              <DateField value={settlementsFilter.to}
                     onChange={(iso) => setSettlementsFilter((f) => ({ ...f, to: iso }))}
                     className="px-2 py-1 border border-slate-200 rounded text-[12px]"
                     data-testid="settlements-filter-to" />
              <button type="button" onClick={clearSettlements}
                      className="px-2 py-1 text-[11px] border border-slate-200 rounded text-slate-600 hover:bg-slate-50"
                      data-testid="settlements-filter-clear">
                {t('adminFinance.filters.clear')}
              </button>
              <span className="ml-auto text-[11px] text-slate-500" data-testid="settlements-count">
                {t('adminFinance.filters.showing', { shown: filteredSettlements.length, total: settlementRows.length })}
              </span>
            </div>
            <div className="overflow-x-auto border border-slate-200 rounded bg-white">
              <table className="w-full text-[12px]" data-testid="admin-finance-settlements-table">
                <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wider">
                  <tr>
                    <SortHeader label={t('adminFinance.settlements.col.requestedAt')} colKey="requested_at"
                                sort={settlementsFilter.sort}
                                setSort={(s) => setSettlementsFilter((f) => ({ ...f, sort: s }))}
                                testid="settlements-sort-date" />
                    <SortHeader label={t('adminFinance.settlements.col.provider')} colKey="provider_name"
                                sort={settlementsFilter.sort}
                                setSort={(s) => setSettlementsFilter((f) => ({ ...f, sort: s }))}
                                testid="settlements-sort-provider" />
                    <SortHeader label={t('adminFinance.settlements.col.patient')} colKey="patient_name"
                                sort={settlementsFilter.sort}
                                setSort={(s) => setSettlementsFilter((f) => ({ ...f, sort: s }))}
                                testid="settlements-sort-patient" />
                    <SortHeader label={t('adminFinance.settlements.col.amount')} colKey="amount_minor_units"
                                align="right" sort={settlementsFilter.sort}
                                setSort={(s) => setSettlementsFilter((f) => ({ ...f, sort: s }))}
                                testid="settlements-sort-amount" />
                    <SortHeader label={t('adminFinance.settlements.col.status')} colKey="status"
                                sort={settlementsFilter.sort}
                                setSort={(s) => setSettlementsFilter((f) => ({ ...f, sort: s }))}
                                testid="settlements-sort-status" />
                    <th className="text-left px-2 py-1.5">{t('adminFinance.settlements.col.booking')}</th>
                    <th className="text-left px-2 py-1.5">{t('adminFinance.settlements.col.pspTransfer')}</th>
                    <th className="text-right px-2 py-1.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSettlements.length === 0 && (
                    <tr><td colSpan={8} className="px-2 py-4 text-center text-slate-500" data-testid="admin-finance-settlements-empty">
                      {t('adminFinance.settlements.empty')}
                    </td></tr>
                  )}
                  {filteredSettlements.map((r) => (
                    <tr key={r.psp_operation_id} className="border-t border-slate-100">
                      <td className="px-2 py-1.5 text-slate-700">{fmtDate(r.requested_at)}</td>
                      <td className="px-2 py-1.5">
                        <div className="text-slate-900 font-medium">{r.provider_name || r.clinic_id || '—'}</div>
                        {(r.provider_name && r.clinic_id) && (
                          <div className="text-[10px] text-slate-400 font-mono">{r.clinic_id}</div>
                        )}
                        <div className="text-[10px] text-slate-400">{r.psp_provider || '—'} · {r.currency || '—'}</div>
                      </td>
                      <td className="px-2 py-1.5">
                        <div className="text-slate-800">{r.patient_name || (r.patient_id ? r.patient_id : '—')}</div>
                        {(r.patient_name && r.patient_id) && (
                          <div className="text-[10px] text-slate-400 font-mono">{r.patient_id}</div>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-right">{fmtMinor(r.amount_minor_units, r.currency)}</td>
                      <td className="px-2 py-1.5"><Pill tone={OP_TONE[r.status] || 'slate'}>{t(`common.status.${r.status}`, { defaultValue: r.status })}</Pill></td>
                      <td className="px-2 py-1.5 text-slate-700 font-mono text-[11px]">{r.booking_id || '—'}</td>
                      <td className="px-2 py-1.5 text-slate-500 font-mono text-[11px]">{r.psp_transfer_id || '—'}</td>
                      <td className="px-2 py-1.5 text-right">
                        {r.clinic_id && r.booking_id && (
                          <button type="button" onClick={() => setDrawer({ clinicId: r.clinic_id, bookingId: r.booking_id })}
                                  className="text-[11px] text-slate-700 hover:text-slate-900 underline"
                                  data-testid={`admin-open-booking-${r.psp_operation_id}`}>
                            {t('adminFinance.settlements.open')}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!loading && !error && tab === 'subscriptions' && (
          <div className="space-y-3" data-testid="admin-finance-subscriptions">
            <p className="text-[11px] text-slate-500">
              {t('adminFinance.subscriptions.note')}
            </p>
            <div className="bg-white border border-slate-200 rounded overflow-x-auto">
              <table className="w-full text-[12px]" data-testid="admin-finance-subscriptions-table">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-2 py-1.5">{t('adminFinance.subscriptions.currency')}</th>
                    <th className="text-right px-2 py-1.5">{t('adminFinance.subscriptions.invoices')}</th>
                    <th className="text-right px-2 py-1.5">{t('adminFinance.subscriptions.renewals')}</th>
                    <th className="text-right px-2 py-1.5">{t('adminFinance.subscriptions.net')}</th>
                    <th className="text-right px-2 py-1.5">{t('adminFinance.subscriptions.vat')}</th>
                    <th className="text-right px-2 py-1.5">{t('adminFinance.subscriptions.gross')}</th>
                    <th className="text-right px-2 py-1.5">{t('adminFinance.subscriptions.collected')}</th>
                    <th className="text-right px-2 py-1.5">{t('adminFinance.subscriptions.open')}</th>
                    <th className="text-right px-2 py-1.5">{t('adminFinance.subscriptions.deferred')}</th>
                    <th className="text-right px-2 py-1.5">{t('adminFinance.subscriptions.recognised')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(overview?.subscriptions?.by_currency || []).map((g) => (
                    <tr key={g.currency} className="border-t border-slate-100"
                        data-testid={`admin-finance-subscriptions-${g.currency}`}>
                      <td className="px-2 py-1.5 font-semibold">{g.currency}</td>
                      <td className="px-2 py-1.5 text-right">{g.invoice_count}</td>
                      <td className="px-2 py-1.5 text-right">{g.renewal_invoices}</td>
                      <td className="px-2 py-1.5 text-right">{g.invoiced_net.toFixed(2)}</td>
                      <td className="px-2 py-1.5 text-right">{g.invoiced_vat.toFixed(2)}</td>
                      <td className="px-2 py-1.5 text-right font-semibold">{g.invoiced_gross.toFixed(2)}</td>
                      <td className="px-2 py-1.5 text-right">{g.collected.toFixed(2)}</td>
                      <td className="px-2 py-1.5 text-right">{g.open_receivable.toFixed(2)}</td>
                      <td className="px-2 py-1.5 text-right">{g.deferred_remaining.toFixed(2)}</td>
                      <td className="px-2 py-1.5 text-right">{g.revenue_recognised.toFixed(2)}</td>
                    </tr>
                  ))}
                  {!(overview?.subscriptions?.by_currency || []).length && (
                    <tr><td colSpan={10} className="px-2 py-4 text-center text-slate-500"
                            data-testid="admin-finance-subscriptions-empty">
                      {t('adminFinance.subscriptions.empty')}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!loading && !error && tab === 'reports' && (
          <AccountingGradeReports scopeLabel={t('adminFinance.tabs.platformScope')} />
        )}

        {!loading && !error && tab === 'receivables' && (
          <CommissionReceivablesPanel />
        )}

        {!loading && !error && tab === 'exceptions' && (
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2 items-center bg-white border border-slate-200 rounded px-2 py-2" data-testid="exceptions-filters">
              <input type="text" value={exceptionsFilter.provider}
                     onChange={(e) => setExceptionsFilter((f) => ({ ...f, provider: e.target.value }))}
                     placeholder={t('adminFinance.filters.providerSearch')}
                     className="px-2 py-1 border border-slate-200 rounded text-[12px] w-44"
                     data-testid="exceptions-filter-provider" />
              <input type="text" value={exceptionsFilter.patient}
                     onChange={(e) => setExceptionsFilter((f) => ({ ...f, patient: e.target.value }))}
                     placeholder={t('adminFinance.filters.patientSearch')}
                     className="px-2 py-1 border border-slate-200 rounded text-[12px] w-44"
                     data-testid="exceptions-filter-patient" />
              <input type="text" value={exceptionsFilter.booking}
                     onChange={(e) => setExceptionsFilter((f) => ({ ...f, booking: e.target.value }))}
                     placeholder={t('adminFinance.filters.bookingSearch')}
                     className="px-2 py-1 border border-slate-200 rounded text-[12px] w-36"
                     data-testid="exceptions-filter-booking" />
              <select value={exceptionsFilter.kind}
                      onChange={(e) => setExceptionsFilter((f) => ({ ...f, kind: e.target.value }))}
                      className="px-2 py-1 border border-slate-200 rounded text-[12px]"
                      data-testid="exceptions-filter-kind">
                <option value="">{t('adminFinance.filters.anyKind')}</option>
                {exceptionKinds.map((k) => (
                  <option key={k} value={k}>{t(`adminFinance.exceptions.kind.${k}`, { defaultValue: k })}</option>
                ))}
              </select>
              <select value={exceptionsFilter.psp}
                      onChange={(e) => setExceptionsFilter((f) => ({ ...f, psp: e.target.value }))}
                      className="px-2 py-1 border border-slate-200 rounded text-[12px]"
                      data-testid="exceptions-filter-psp">
                <option value="">{t('adminFinance.filters.anyPsp')}</option>
                {exceptionPsps.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              <select value={exceptionsFilter.currency}
                      onChange={(e) => setExceptionsFilter((f) => ({ ...f, currency: e.target.value }))}
                      className="px-2 py-1 border border-slate-200 rounded text-[12px]"
                      data-testid="exceptions-filter-currency">
                <option value="">{t('adminFinance.filters.anyCurrency')}</option>
                {exceptionCurrencies.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <label className="text-[11px] text-slate-500">{t('adminFinance.filters.dateFrom')}</label>
              <DateField value={exceptionsFilter.from}
                     onChange={(iso) => setExceptionsFilter((f) => ({ ...f, from: iso }))}
                     className="px-2 py-1 border border-slate-200 rounded text-[12px]"
                     data-testid="exceptions-filter-from" />
              <label className="text-[11px] text-slate-500">{t('adminFinance.filters.dateTo')}</label>
              <DateField value={exceptionsFilter.to}
                     onChange={(iso) => setExceptionsFilter((f) => ({ ...f, to: iso }))}
                     className="px-2 py-1 border border-slate-200 rounded text-[12px]"
                     data-testid="exceptions-filter-to" />
              <button type="button" onClick={clearExceptions}
                      className="px-2 py-1 text-[11px] border border-slate-200 rounded text-slate-600 hover:bg-slate-50"
                      data-testid="exceptions-filter-clear">
                {t('adminFinance.filters.clear')}
              </button>
              <span className="ml-auto text-[11px] text-slate-500" data-testid="exceptions-count">
                {t('adminFinance.filters.showing', { shown: filteredExceptions.length, total: exceptionRows.length })}
              </span>
            </div>
            <div className="overflow-x-auto border border-slate-200 rounded bg-white">
              <table className="w-full text-[12px]" data-testid="admin-finance-exceptions-table">
                <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wider">
                  <tr>
                    <SortHeader label={t('adminFinance.exceptions.col.at')} colKey="at_utc"
                                sort={exceptionsFilter.sort}
                                setSort={(s) => setExceptionsFilter((f) => ({ ...f, sort: s }))}
                                testid="exceptions-sort-date" />
                    <SortHeader label={t('adminFinance.exceptions.col.provider')} colKey="provider_name"
                                sort={exceptionsFilter.sort}
                                setSort={(s) => setExceptionsFilter((f) => ({ ...f, sort: s }))}
                                testid="exceptions-sort-provider" />
                    <SortHeader label={t('adminFinance.exceptions.col.patient')} colKey="patient_name"
                                sort={exceptionsFilter.sort}
                                setSort={(s) => setExceptionsFilter((f) => ({ ...f, sort: s }))}
                                testid="exceptions-sort-patient" />
                    <SortHeader label={t('adminFinance.exceptions.col.kind')} colKey="kind"
                                sort={exceptionsFilter.sort}
                                setSort={(s) => setExceptionsFilter((f) => ({ ...f, sort: s }))}
                                testid="exceptions-sort-kind" />
                    <SortHeader label={t('adminFinance.exceptions.col.amount')} colKey="amount_minor_units"
                                align="right" sort={exceptionsFilter.sort}
                                setSort={(s) => setExceptionsFilter((f) => ({ ...f, sort: s }))}
                                testid="exceptions-sort-amount" />
                    <th className="text-left px-2 py-1.5">{t('adminFinance.exceptions.col.booking')}</th>
                    <th className="text-left px-2 py-1.5">{t('adminFinance.exceptions.col.detail')}</th>
                    <th className="text-right px-2 py-1.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredExceptions.length === 0 && (
                    <tr><td colSpan={8} className="px-2 py-4 text-center text-slate-500" data-testid="admin-finance-exceptions-empty">
                      {t('adminFinance.exceptions.emptyPlain')}
                    </td></tr>
                  )}
                  {filteredExceptions.map((r) => (
                    <tr key={r.ref} className="border-t border-slate-100">
                      <td className="px-2 py-1.5 text-slate-700">{fmtDate(r.at_utc)}</td>
                      <td className="px-2 py-1.5">
                        <div className="text-slate-900 font-medium">{r.provider_name || r.clinic_id || '—'}</div>
                        {(r.provider_name && r.clinic_id) && (
                          <div className="text-[10px] text-slate-400 font-mono">{r.clinic_id}</div>
                        )}
                        <div className="text-[10px] text-slate-400">{r.psp_provider || '—'} · {r.currency || '—'} · {r.operation_type || '—'}</div>
                      </td>
                      <td className="px-2 py-1.5">
                        <div className="text-slate-800">{r.patient_name || (r.patient_id ? r.patient_id : '—')}</div>
                        {(r.patient_name && r.patient_id) && (
                          <div className="text-[10px] text-slate-400 font-mono">{r.patient_id}</div>
                        )}
                      </td>
                      <td className="px-2 py-1.5"><Pill tone="rose">{t(`adminFinance.exceptions.kind.${r.kind}`, { defaultValue: r.kind })}</Pill></td>
                      <td className="px-2 py-1.5 text-right">{r.amount_minor_units ? fmtMinor(r.amount_minor_units, r.currency) : '—'}</td>
                      <td className="px-2 py-1.5 text-slate-700 font-mono text-[11px]">{r.booking_id || '—'}</td>
                      <td className="px-2 py-1.5 text-rose-700 max-w-[240px] truncate" title={r.detail || ''}>{r.detail || '—'}</td>
                      <td className="px-2 py-1.5 text-right">
                        {r.clinic_id && r.booking_id && (
                          <button type="button" onClick={() => setDrawer({ clinicId: r.clinic_id, bookingId: r.booking_id })}
                                  className="text-[11px] text-slate-700 hover:text-slate-900 underline"
                                  data-testid={`admin-open-exception-${r.ref}`}>
                            {t('adminFinance.exceptions.inspect')}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-3 py-2 text-[11px] text-slate-500 border-t border-slate-100" data-testid="admin-finance-exceptions-helper">
                {t('adminFinance.exceptions.recoveryHelperPlain')}
              </div>
            </div>
          </div>
        )}
      </div>

      {drawer && <AdminBookingDrawer clinicId={drawer.clinicId} bookingId={drawer.bookingId} onClose={() => setDrawer(null)} />}
    </div>
  );
};

export default AdminFinance;